/* eslint-disable no-prototype-builtins */
import {
  AmlHistoryResponse,
  ExploreConnectionResponse,
  InvestigationHistoryItem,
  TransactionDirectionEnum,
} from '@/api';
import {
  getFormattedAmount,
  getRiskScoreValue,
  getAmountPrecision,
} from '@/helpers';
import { AssetsEnum, ChainsDictionary } from '@/config';
import {
  InvestigationHistoryData,
  InvestigationHistoryItemData,
} from '../models';

type ItemConnection = {
  entity: Record<string, any>;
  value: number;
  rawValue: number;
  caption: string;
  isMuted: boolean;
  path: Record<string, any> | null;
  direction: TransactionDirectionEnum;
  pathAddress: string[];
  clusterAddress: string | null;
  title: string;
  description: string;
};

const getAssetPrecicion = (item: InvestigationHistoryItem) => {
  return getAmountPrecision(item);
};

const getRawAmount = (amount: number, precision: number) => {
  return amount / precision;
};

const connectionNaming = (entity: Record<string, any> | null) => {
  let title = entity?.name || entity?.slug || '';
  let description = (entity?.type || '').split('_').join(' ');
  const unknownClusterSuffix = 'Unknown Cluster';
  const unknownClusterDescription = 'unknown clusters';

  switch (title) {
    case 'yellow_side': {
      title = `Mid Risk ${unknownClusterSuffix}`;
      description = unknownClusterDescription;
      break;
    }
    case 'green_side': {
      title = `Low Risk ${unknownClusterSuffix}`;
      description = unknownClusterDescription;
      break;
    }
    case 'red_side': {
      title = `High Risk ${unknownClusterSuffix}`;
      description = unknownClusterDescription;
      break;
    }
    default: {
      break;
    }
  }

  return { title, description };
};

const connectionIsMuted = (con: Record<string, any>) => {
  return !con.entity?.id;
};

const fetchDirectedAddresses = (
  tx: Record<string, any>,
  txSide: string,
  owner: string,
) => {
  const addresses = [];
  const data = Array.isArray(tx[txSide]) ? tx[txSide] : [tx[txSide]];

  for (const j in data) {
    if (
      data[j].owner instanceof Object &&
      data[j].owner.id === owner &&
      addresses.indexOf(data[j].address) === -1
    ) {
      addresses.push(data[j].address);
    }
  }
  return addresses;
};

const findClusterAddress = (
  path: Record<string, any> | null,
  direction: TransactionDirectionEnum,
  txs: Record<string, any>,
) => {
  if (!path || !path[direction]) return null;

  let clusterAddress = 'No address data';
  const txDataKey =
    direction !== TransactionDirectionEnum.SENT ? 'inputs' : 'outputs';
  const pathStart = path[direction][0];

  if (
    pathStart instanceof Object &&
    pathStart.hasOwnProperty('tx') &&
    pathStart.tx.length &&
    txs instanceof Object &&
    txs.hasOwnProperty(pathStart.tx) &&
    txs[pathStart.tx].hasOwnProperty(txDataKey) &&
    txs[pathStart.tx][txDataKey].length &&
    txs[pathStart.tx][txDataKey][0].hasOwnProperty('address')
  ) {
    clusterAddress = txs[pathStart.tx][txDataKey][0].address;
  }

  return clusterAddress;
};

const findPathAddresses = (
  path: Record<string, any> | null,
  direction: TransactionDirectionEnum,
  txs: Record<string, any>,
  owner: string,
): Record<string, any> => {
  const clusterAddress = findClusterAddress(path, direction, txs);
  return { pathAddress: null, clusterAddress };

  /**
   * Txs are absent in current data provider response
   *
   */
  /**
  if (
    !path ||
    !path[direction] ||
    !path[direction][0] ||
    !path[direction][0][0] ||
    !path[direction][0][0].txs
  ) {
    return { pathAddress: null, clusterAddress };
  }

  
  let addressList: string[] = [];
  let firstNodeTx = path[direction][0].tx;

  if (
    firstNodeTx instanceof Object &&
    firstNodeTx.hasOwnProperty('root_hash')
  ) {
    firstNodeTx = firstNodeTx.root_hash;
  }

  
  
  if (txs.hasOwnProperty(firstNodeTx)) {
    let txSides =
      direction === TransactionDirectionEnum.SENT
        ? ['outputs', 'inputs']
        : ['inputs', 'outputs'];

    if (txs[firstNodeTx].hasOwnProperty(txSides[0])) {
      addressList = fetchDirectedAddresses(txs[firstNodeTx], txSides[0], owner);
      if (!addressList.length) {
        addressList = fetchDirectedAddresses(
          txs[firstNodeTx],
          txSides[1],
          owner,
        );
      }
    } else {
      txSides =
        direction === TransactionDirectionEnum.SENT
          ? ['output', 'input']
          : ['input', 'output'];

      if (txs[firstNodeTx].hasOwnProperty(txSides[0])) {
        addressList = fetchDirectedAddresses(
          txs[firstNodeTx],
          txSides[0],
          owner,
        );

        if (!addressList.length) {
          addressList = fetchDirectedAddresses(
            txs[firstNodeTx],
            txSides[1],
            owner,
          );
        }
      }
    }
  }
  return {
    pathAddress: addressList.length ? addressList : null,
    clusterAddress,
  };
   */
};

const prepareConnectionList = (item: InvestigationHistoryItem) => {
  if (!item.indirects) return null;

  const tempDirectIncomes: any = [];
  const tempIndirectIncomes: any = [];
  const tempDirectOutcomes: any = [];
  const tempIndirectOutcomes: any = [];
  const precision = getAssetPrecicion(item);

  item.indirects.connections.forEach((connection: any) => {
    if (connection[TransactionDirectionEnum.RECEIVED].total > 0) {
      if (
        connection[TransactionDirectionEnum.RECEIVED].hops <= 1 &&
        connection[TransactionDirectionEnum.RECEIVED].hops !== null
      ) {
        tempDirectIncomes.push(connection);
      } else if (
        connection[TransactionDirectionEnum.RECEIVED].hops > 1 ||
        connection[TransactionDirectionEnum.RECEIVED].hops === null
      ) {
        tempIndirectIncomes.push(connection);
      }
    }

    if (connection[TransactionDirectionEnum.SENT].total > 0) {
      if (
        connection[TransactionDirectionEnum.SENT].hops <= 1 &&
        connection[TransactionDirectionEnum.SENT].hops !== null
      ) {
        tempDirectOutcomes.push(connection);
      } else if (
        connection[TransactionDirectionEnum.SENT].hops > 1 ||
        connection[TransactionDirectionEnum.SENT].hops === null
      ) {
        tempIndirectOutcomes.push(connection);
      }
    }
  });

  tempDirectIncomes.sort((con1: any, con2: any) => {
    if ((con1.entity?.riskscore || 0) > (con2.entity?.riskscore || 0))
      return -1;
    if (
      con1.entity?.riskscore === con2.entity?.riskscore &&
      con1[TransactionDirectionEnum.RECEIVED].total >
        con2[TransactionDirectionEnum.RECEIVED].total
    ) {
      return -1;
    }

    return 1;
  });

  tempIndirectIncomes.sort((con1: any, con2: any) => {
    const con1Hops = con1[TransactionDirectionEnum.RECEIVED].hops;
    const con2Hops = con2[TransactionDirectionEnum.RECEIVED].hops;

    if (con2Hops === null && con1Hops !== null) return -1;
    if (con1Hops === null && con2Hops !== null) return 1;
    if (con1Hops < con2Hops) return -1;
    if (con1Hops === con2Hops) {
      if (
        (con1.entity?.riskscore || 0) > (con2.entity?.riskscore || 0) ||
        (con1.entity?.riskscore === con2.entity?.riskscore &&
          con1[TransactionDirectionEnum.RECEIVED].total >
            con2[TransactionDirectionEnum.RECEIVED].total)
      ) {
        return -1;
      }
    }

    return 1;
  });

  tempDirectOutcomes.sort((con1: any, con2: any) => {
    if ((con1.entity?.riskscore || 0) > (con2.entity?.riskscore || 0))
      return -1;
    if (
      con1.entity?.riskscore === con2.entity?.riskscore &&
      con1[TransactionDirectionEnum.SENT].total >
        con2[TransactionDirectionEnum.SENT].total
    ) {
      return -1;
    }

    return 1;
  });

  tempIndirectOutcomes.sort((con1: any, con2: any) => {
    const con1Hops = con1[TransactionDirectionEnum.SENT].hops;
    const con2Hops = con2[TransactionDirectionEnum.SENT].hops;

    if (con2Hops === null && con1Hops !== null) return -1;
    if (con1Hops === null && con2Hops !== null) return 1;
    if (con1Hops < con2Hops) return -1;
    if (con1Hops === con2Hops) {
      if (
        (con1.entity?.riskscore || 0) > (con2.entity?.riskscore || 0) ||
        (con1.entity?.riskscore === con2.entity?.riskscore &&
          con1[TransactionDirectionEnum.SENT].total >
            con2[TransactionDirectionEnum.SENT].total)
      ) {
        return -1;
      }
    }

    return 1;
  });

  const directIncomes: ItemConnection = tempDirectIncomes.map((con: any) => {
    const path = item.paths ? item.paths[con.entity?.id] : {};

    return {
      entity: con.entity || {},
      address: con.address || '',
      value: getFormattedAmount(con.received.total, precision),
      rawValue: getRawAmount(con.received.total, precision),
      caption: 'Direct',
      isMuted: connectionIsMuted(con),
      path: path ? path[TransactionDirectionEnum.RECEIVED] : null,
      direction: TransactionDirectionEnum.RECEIVED,
      ...findPathAddresses(
        path,
        TransactionDirectionEnum.RECEIVED,
        item.txs || {},
        con.entity?.global_id,
      ),
      ...connectionNaming(con.entity),
    };
  });

  const indirectIncomes: ItemConnection = tempIndirectIncomes.map(
    (con: any) => {
      const path = item.paths ? item.paths[con.entity?.id] : {};

      return {
        entity: con.entity || {},
        address: con.address || '',
        value: getFormattedAmount(
          con[TransactionDirectionEnum.RECEIVED].total,
          precision,
        ),
        rawValue: getRawAmount(
          con[TransactionDirectionEnum.RECEIVED].total,
          precision,
        ),
        caption: `Routing: ${
          con[TransactionDirectionEnum.RECEIVED].hops || '-'
        }`,
        isMuted: connectionIsMuted(con),
        path: path ? path[TransactionDirectionEnum.RECEIVED] : null,
        direction: TransactionDirectionEnum.RECEIVED,
        ...findPathAddresses(
          path,
          TransactionDirectionEnum.RECEIVED,
          item.txs || {},
          con.entity?.id,
        ),
        ...connectionNaming(con.entity),
      };
    },
  );

  const directOutcomes: ItemConnection = tempDirectOutcomes.map((con: any) => {
    const path = item.paths ? item.paths[con.entity?.id] : {};

    return {
      entity: con.entity || {},
      address: con.address || '',
      value: getFormattedAmount(
        con[TransactionDirectionEnum.SENT].total,
        precision,
      ),
      rawValue: getRawAmount(
        con[TransactionDirectionEnum.SENT].total,
        precision,
      ),
      caption: 'Direct',
      isMuted: connectionIsMuted(con),
      path: path ? path[TransactionDirectionEnum.SENT] : null,
      direction: TransactionDirectionEnum.SENT,
      ...findPathAddresses(
        path,
        TransactionDirectionEnum.SENT,
        item.txs || {},
        con.entity?.id,
      ),
      ...connectionNaming(con.entity),
    };
  });

  const indirectOutcomes: ItemConnection = tempIndirectOutcomes.map(
    (con: any) => {
      const path = item.paths ? item.paths[con.entity?.id] : {};

      return {
        entity: con.entity || {},
        address: con.address || '',
        value: getFormattedAmount(
          con[TransactionDirectionEnum.SENT].total,
          precision,
        ),
        rawValue: getRawAmount(
          con[TransactionDirectionEnum.SENT].total,
          precision,
        ),
        caption: `Routing: ${con[TransactionDirectionEnum.SENT].hops || ''}`,
        isMuted: connectionIsMuted(con),
        path: path ? path[TransactionDirectionEnum.SENT] : null,
        direction: TransactionDirectionEnum.SENT,
        ...findPathAddresses(
          path,
          TransactionDirectionEnum.SENT,
          item.txs || {},
          con.entity?.id,
        ),
        ...connectionNaming(con.entity),
      };
    },
  );

  return { directIncomes, indirectIncomes, directOutcomes, indirectOutcomes };
};

const normalizeTimestamp = (timestamp: string) => {
  if (timestamp.slice(-1).toLowerCase() !== 'z') {
    return `${timestamp}z`;
  }
  return timestamp;
};

const detectAsset = (item: InvestigationHistoryItem) => {
  if (item?.tokenData?.code) {
    return item.tokenData.code;
  }
  return item?.asset && ChainsDictionary[item.asset]
    ? ChainsDictionary[item.asset].symbol
    : item?.asset;
};

const mapInvestigationItem = (
  item: InvestigationHistoryItem,
): InvestigationHistoryItemData => {
  const mappedItem: InvestigationHistoryItemData = {
    data: item.data,
    indirects: item.indirects,
    connections: prepareConnectionList(item),
    baseData: item.baseData,
    sourceData: item.sourceData,
    tokenData: item.tokenData,
    displayAsset: detectAsset(item),
    counterparty: item.counterparty,
    xlsReport: item.xlsReport,
    investigationLink: item.investigationLink,
    stats: item.stats,
    identifier: item.identifier,
    uuid: item.uid,
    address: item.address || item.data?.address,
    riskscore: getRiskScoreValue(item.riskscore),
    asset: item.asset,
    network: item.network,
    flow: item.flow,
    status: item.status,
    timestamp: +new Date(normalizeTimestamp(item.timestamp)),
    type: item._type,
    paths: item.paths,
    txs: item.txs,
  };
  return mappedItem;
};

const investigationHistory = (
  response: AmlHistoryResponse,
): InvestigationHistoryData => {
  const { data } = response;
  const list = data.data as InvestigationHistoryItem[];
  const mappedData: InvestigationHistoryData = {
    result: data.result,
    pageLimit: data.pageLimit,
    totalCount: data.totalCount,
    balance: data.balance,
    amlFlow: data.amlFlow,
    discount: data.discount,
    discountDueTime: data.discountDueTime,
    promoFlow: data.promoFlow,
    usePromoFlow: data.usePromoFlow,
    devModeUsed: !!data?.devModeUsed,
    items: list.map(mapInvestigationItem),
  };

  return mappedData;
};

const handleInvestigationHistoryItem = (
  response: ExploreConnectionResponse,
): InvestigationHistoryItemData => {
  const { data } = response;
  return mapInvestigationItem(data.data);
};

export {
  investigationHistory,
  handleInvestigationHistoryItem,
  mapInvestigationItem,
};
